import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const article = articles.find((a) => a.id === Number(id));

  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  const hotArticles = articles.filter((a) => a.isHot).slice(0, 5);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 黑料天天动漫`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.excerpt);
      }
    }
  }, [article]);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="container-custom text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该文章不存在或已被删除</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '黑料天天动漫',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heimanime.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://heimanime.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <Navbar />

      <main>
        {/* Article Header */}
        <section className="pt-20 pb-0">
          <div className="container-custom">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </section>

        {/* Article Content */}
        <article className="py-6 md:py-8">
          <div className="container-custom">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <div className="flex-1 max-w-3xl">
                {/* Title & Meta */}
                <header className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-500 text-background-50">
                      {article.categoryName}
                    </span>
                    {article.isHot && (
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50 flex items-center gap-1">
                        <i className="ri-fire-line text-xs"></i> 热门
                      </span>
                    )}
                  </div>
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100">
                        <i className="ri-user-line text-xs text-secondary-600"></i>
                      </div>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line text-foreground-400"></i>
                      {article.publishedAt}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-eye-line text-foreground-400"></i>
                      {formatViews(article.views)} 阅读
                    </span>
                  </div>
                </header>

                {/* Cover Image */}
                <div className="rounded-xl overflow-hidden mb-8">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`${article.title} - 黑料天天动漫`}
                    className="w-full h-auto object-cover"
                  />
                </div>

                {/* Article Body */}
                <div
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="mt-8 pt-6 border-t border-background-200/70">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-foreground-500">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="tag-pill px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 cursor-pointer hover:bg-secondary-200 transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Share */}
                <div className="mt-6 flex items-center gap-3">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </a>
                  <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </a>
                  <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </a>
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                  <section className="mt-12 pt-8 border-t border-background-200/70">
                    <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">相关文章</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {relatedArticles.map((ra) => (
                        <ArticleCard key={ra.id} {...ra} variant="default" />
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-fire-line text-primary-600"></i>
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground-900">热门爆料</h3>
                    </div>
                    <div className="space-y-1">
                      {hotArticles.map((ha) => (
                        <ArticleCard key={ha.id} {...ha} variant="compact" />
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
}